import React from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Back / Forward are the browser's own history. React Router stamps each entry with an index
// (history.state.idx); we remember the furthest index reached (so Forward knows when nothing is
// ahead) and the URL at each index (so a breadcrumb can tell whether "Back" lands on its list).
const MAX_KEY = "abs-nav-max-idx";
const URLS_KEY = "abs-nav-urls";
let lastKey = null;
let maxIdx = Number(sessionStorage.getItem(MAX_KEY) || 0);
let urls = (() => { try { return JSON.parse(sessionStorage.getItem(URLS_KEY) || "{}"); } catch { return {}; } })();

const currentIdx = () => window.history.state?.idx ?? 0;
export const urlAt = (idx) => urls[idx];

export function useNavHistory() {
  const location = useLocation();
  const navType = useNavigationType();
  const navigate = useNavigate();
  const idx = currentIdx();
  if (location.key !== lastKey) {
    lastKey = location.key;
    maxIdx = navType === "PUSH" ? idx : Math.max(maxIdx, idx);
    urls[idx] = location.pathname + location.search;
    if (navType === "PUSH") Object.keys(urls).forEach((k) => { if (+k > idx) delete urls[k]; });
    sessionStorage.setItem(MAX_KEY, String(maxIdx));
    sessionStorage.setItem(URLS_KEY, JSON.stringify(urls));
  }
  return {
    back: () => idx > 0 && navigate(-1),
    forward: () => idx < maxIdx && navigate(1),
    canBack: idx > 0,
    canForward: idx < maxIdx,
  };
}

// Kept so existing wrappers keep working — history lives in the browser, not here.
export function NavHistoryProvider({ children }) {
  return children;
}

const BTN = "inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors";

// Phones only: Back / Forward in the page header. Desktop uses breadcrumbs + the browser buttons.
export function NavHistoryControls({ className = "" }) {
  const { back, forward, canBack, canForward } = useNavHistory();
  return (
    <div className={`inline-flex shrink-0 items-center ${className}`} data-testid="nav-history-controls">
      <button type="button" onClick={back} disabled={!canBack} title={canBack ? "Back" : "No earlier page"}
              aria-label="Back" data-testid="nav-history-back" className={BTN}>
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button type="button" onClick={forward} disabled={!canForward} title={canForward ? "Forward" : "No later page"}
              aria-label="Forward" data-testid="nav-history-forward" className={BTN}>
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}
